/** React binding for RoomClient: snapshot, connection status, errors, emotes, Tibbs's readout messages. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { RoomClient } from '../net/roomClient';
import type { ConnectionStatus, FatalReason, ReadoutMessage, RoomSnapshot } from '../net/roomClient';
import type { Emote, SeatView } from '../../worker/protocol';
import type { SeatInfo } from './types';

export interface EmoteEvent {
  id: number;
  seat: number;
  emote: Emote;
}

/** A readout message from the room, numbered so the table handles each one once. */
export type ReadoutEvent = ReadoutMessage & { id: number };

export interface RoomHook {
  client: RoomClient | null;
  snapshot: RoomSnapshot | null;
  /** Local clock (ms) when the snapshot arrived; anchors the idle-turn countdowns. */
  receivedAt: number;
  status: ConnectionStatus;
  fatal: FatalReason | null;
  error: { id: number; message: string } | null;
  emotes: EmoteEvent[];
  /** Recent readout messages (the last 40), oldest first. */
  readoutEvents: ReadoutEvent[];
}

export function useRoom(code: string | null, name: string | null): RoomHook {
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null);
  const [receivedAt, setReceivedAt] = useState(0);
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [fatal, setFatal] = useState<FatalReason | null>(null);
  const [error, setError] = useState<RoomHook['error']>(null);
  const [emotes, setEmotes] = useState<EmoteEvent[]>([]);
  const [readoutEvents, setReadoutEvents] = useState<ReadoutEvent[]>([]);
  const seq = useRef(0);

  const client = useMemo(() => {
    if (!code || !name) return null;
    try {
      return new RoomClient({ code, name });
    } catch {
      return null;
    }
  }, [code, name]);

  useEffect(() => {
    if (!client) return;
    setSnapshot(null);
    setFatal(null);
    const offs = [
      client.subscribe((snap) => {
        setSnapshot(snap);
        setReceivedAt(Date.now());
      }),
      client.on('status', (s, f) => {
        setStatus(s);
        if (f && f !== 'closed-by-client') setFatal(f);
      }),
      client.on('error', (message) => setError({ id: ++seq.current, message })),
      client.on('emote', (seat, emote) => {
        const ev = { id: ++seq.current, seat, emote };
        setEmotes((list) => [...list.slice(-8), ev]);
      }),
      client.on('readout', (msg) => {
        const ev = { ...msg, id: ++seq.current } as ReadoutEvent;
        setReadoutEvents((list) => [...list.slice(-39), ev]);
      }),
    ];
    client.connect();
    return () => {
      offs.forEach((off) => off());
      client.close();
    };
  }, [client]);

  return { client, snapshot, receivedAt, status, fatal, error, emotes, readoutEvents };
}

export function seatInfoFromView(v: SeatView, receivedAt = Date.now()): SeatInfo {
  return {
    name: v.name,
    isBot: v.isBot,
    ...(v.botLevel ? { level: v.botLevel } : {}),
    connected: v.connected,
    autopilot: v.autopilot,
    ready: v.ready,
    turnDeadline: typeof v.turnMsLeft === 'number' ? receivedAt + v.turnMsLeft : null,
    ...(v.stepIn ? { stepIn: true } : {}),
  };
}
